/**
 * Contrato de remitos ANMAT pendientes de confirmar (recepción).
 *
 * El backend pega a riga-traza-v2 → ANMAT
 * (`GET /api/in/transacciones-no-confirmadas-agrupadas?max_remitos=N`) y devuelve
 * remitos agrupados: una fila por remito, con sus series en `series[]`.
 * En el front 1 card == 1 remito.
 */

import { z } from 'zod';

export const RecepcionSerieSchema = z.object({
  idTransaccion: z.string().nullable(),
  idTransaccionGlobal: z.string().nullable(),
  idEvento: z.string().nullable(),
  descripcionEvento: z.string().nullable(),
  /** DD/MM/YYYY */
  fechaEvento: z.string().nullable(),
  /** DD/MM/YYYY HH:mm */
  fechaTransaccion: z.string().nullable(),
  gtin: z.string().nullable(),
  nombre: z.string().nullable(),
  lote: z.string().nullable(),
  numeroSerial: z.string().nullable(),
  vencimiento: z.string().nullable(),
});

/** Resumen del intento local previo (anmat_task). Null si nunca se trazó. */
export const LocalTaskSummarySchema = z.object({
  /** id del acta local (`anmat_task.id`): con esto se abre directo. */
  id: z.number().int(),
  estado: z.string(),
  glnOrigen: z.string().nullable().optional(),
  stockDestino: z.string().nullable().optional(),
  tracedAt: z.string().nullable(),
  cerradoAt: z.string().nullable().optional(),
  updatedAt: z.string().nullable(),
  totales: z.object({
    trazado: z.number().int().min(0),
    error: z.number().int().min(0),
    pendiente: z.number().int().min(0),
    marcada: z.number().int().min(0).optional(),
    noRecibida: z.number().int().min(0).optional(),
    anulada: z.number().int().min(0).optional(),
    total: z.number().int().min(0),
  }),
});

export const RecepcionRemitoSchema = z.object({
  numeroRemito: z.string().nullable(),
  numeroFactura: z.string().nullable(),
  glnOrigen: z.string().nullable(),
  glnDestino: z.string().nullable(),
  razonSocialOrigen: z.string().nullable(),
  razonSocialDestino: z.string().nullable(),
  cantSeries: z.number().int().min(0),
  series: z.array(RecepcionSerieSchema),
  /** Si existe un anmat_task local con el mismo remito/factura, lo
   *  adjuntamos para que el front pinte el badge "Error" / "Pendiente". */
  localTask: LocalTaskSummarySchema.nullable().optional(),
});

export const GetRecepcionPendientesResponse = z.object({
  remitos: z.array(RecepcionRemitoSchema),
  totalRemitos: z.number().int().min(0),
  totalSeries: z.number().int().min(0),
  remitosDevueltos: z.number().int().min(0),
});

export type RecepcionSerie = z.infer<typeof RecepcionSerieSchema>;
export type RecepcionRemito = z.infer<typeof RecepcionRemitoSchema>;
export type LocalTaskSummary = z.infer<typeof LocalTaskSummarySchema>;
export type GetRecepcionPendientes = z.infer<typeof GetRecepcionPendientesResponse>;
